/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useEffect } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import usePromotions from '../hooks/usePromotions';
import useBrandMetrics from '../hooks/useBrandMetrics';
import SimpleBrandSelector from '../components/SimpleBrandSelector';
import CompetitiveIntelligenceDashboard from '../components/CompetitiveIntelligenceDashboard';
import MarketOverviewDashboard from '../components/MarketOverviewDashboard';
import DateFilter from '../components/DateFilter';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useDateFilter } from '../contexts/DateFilterContext';
import { isDemoMode } from '../services';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const PromotionsPageV2 = () => {
  const { isDark } = useTheme();
  const { selectedDate, formatDate } = useDateFilter();
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const { allBrands } = useBrandMetrics(selectedBrand);
  const { channels, content, loading } = usePromotions(selectedBrand);
  const [activeTab, setActiveTab] = useState<'market' | 'intelligence' | 'content' | 'channels'>('market');
  const [marketData, setMarketData] = useState<any>(null);
  const [brandsWithContent, setBrandsWithContent] = useState<Map<string, number>>(new Map());

  // Fetch market data and set smart default brand
  useEffect(() => {
    fetchMarketData();
  }, []);

  const fetchMarketData = async () => {
    try {
      let data;

      if (isDemoMode) {
        // Mock market data for demo mode
        data = {
          total_brands: 5,
          total_content_pieces: 120,
          brands_with_content: [
            ['Coca-Cola', 35],
            ['Pepsi', 28],
            ['Sprite', 22],
            ['Fanta', 18],
            ['Mountain Dew', 17]
          ],
          market_insights: {
            market_leader: 'Coca-Cola',
            avg_content_per_brand: 24,
            top_channel: 'Instagram',
            avg_engagement: 4.8
          }
        };
      } else {
        const response = await axios.get('/api/content-intelligence/market-overview');
        data = response.data;
      }

      setMarketData(data);

      // Create map of brands with content
      const contentMap = new Map<string, number>();
      if (data.brands_with_content) {
        data.brands_with_content.forEach(([brand, count]: [string, number]) => {
          // Convert brand name to ID format
          const brandId = brand.toLowerCase().replace(/ /g, '-');
          contentMap.set(brandId, count);
        });
      }
      setBrandsWithContent(contentMap);

      // Set default to brand with most content if available
      if (data.market_insights?.market_leader && allBrands.length > 0) {
        const leaderId = data.market_insights.market_leader.toLowerCase().replace(/ /g, '-');
        const matchingBrand = allBrands.find(b =>
          b.brand_id.toLowerCase() === leaderId ||
          b.brand_id.toLowerCase().includes(leaderId.split('-')[0])
        );
        if (matchingBrand) {
          setSelectedBrand(matchingBrand.brand_id);
        } else if (allBrands.length > 0) {
          setSelectedBrand(allBrands[0].brand_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch market data:', error);
      // Fallback to first brand
      if (allBrands.length > 0 && !selectedBrand) {
        setSelectedBrand(allBrands[0].brand_id);
      }
    }
  };

  // Set default brand when brands load
  useEffect(() => {
    if (allBrands.length > 0 && !selectedBrand && marketData) {
      // Try to select brand with content
      const brandWithContent = allBrands.find(b => brandsWithContent.get(b.brand_id));
      if (brandWithContent) {
        setSelectedBrand(brandWithContent.brand_id);
      } else {
        setSelectedBrand(allBrands[0].brand_id);
      }
    }
  }, [allBrands, selectedBrand, marketData, brandsWithContent]);

  const handleBrandSelectFromMarket = (brandId: string) => {
    setSelectedBrand(brandId);
    setActiveTab('intelligence');
  };

  // Theme-aware chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: isDark ? '#E5E7EB' : '#374151'
        }
      },
      tooltip: {
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: isDark ? '#fff' : '#1F2937',
        bodyColor: isDark ? '#E5E7EB' : '#374151'
      }
    },
    scales: {
      x: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563'
        }
      },
      y: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563'
        }
      }
    }
  };

  // Channel Performance Chart
  const channelData = channels.length > 0 ? {
    labels: channels.map(c => c.channel),
    datasets: [{
      label: 'Impressions',
      data: channels.map(c => c.impressions),
      backgroundColor: 'rgba(59, 130, 246, 0.6)',
      borderColor: 'rgba(59, 130, 246, 1)',
      borderWidth: 1
    }, {
      label: 'Conversions',
      data: channels.map(c => c.conversions),
      backgroundColor: 'rgba(16, 185, 129, 0.6)',
      borderColor: 'rgba(16, 185, 129, 1)',
      borderWidth: 1
    }]
  } : null;

  // Emotional Tone Distribution
  const emotionData = content?.emotional_tone_distribution ? {
    labels: Object.keys(content.emotional_tone_distribution),
    datasets: [{
      data: Object.values(content.emotional_tone_distribution),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(147, 51, 234, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(251, 146, 60, 0.8)',
        'rgba(16, 185, 129, 0.8)'
      ],
      borderWidth: 0
    }]
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Promotions & Campaigns</h1>
            <p className="text-text-muted">Competitive content intelligence and market positioning insights</p>
          </div>
          <div className="flex items-center space-x-3">
            <DateFilter />
            <SimpleBrandSelector
              brands={allBrands}
              selectedBrand={selectedBrand}
              onBrandSelect={setSelectedBrand}
            />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mt-6 border-b border-border">
          <button
            onClick={() => setActiveTab('market')}
            className={`px-4 py-3 font-medium transition-all text-sm relative ${
              activeTab === 'market'
                ? 'text-primary'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            <i className="fas fa-globe mr-2"></i>
            Market Overview
            {marketData && (
              <span className="ml-2 px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded">
                {marketData.total_brands}
              </span>
            )}
            {activeTab === 'market' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('intelligence')}
            className={`px-4 py-3 font-medium transition-all text-sm relative ${
              activeTab === 'intelligence'
                ? 'text-primary'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            <i className="fas fa-chart-network mr-2"></i>
            Competitive Intelligence
            {activeTab === 'intelligence' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`px-4 py-3 font-medium transition-all text-sm relative ${
              activeTab === 'content'
                ? 'text-primary'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            <i className="fas fa-images mr-2"></i>
            Content Analysis
            {activeTab === 'content' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('channels')}
            className={`px-4 py-3 font-medium transition-all text-sm relative ${
              activeTab === 'channels'
                ? 'text-primary'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            <i className="fas fa-broadcast-tower mr-2"></i>
            Communication Channels
            {activeTab === 'channels' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        </div>
      </div>

      {/* Content Area with Coming Soon Lock */}
      <div className="relative min-h-[500px]">
        {/* Blurred Content Preview */}
        <div className="filter blur-sm opacity-50 pointer-events-none">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-text-muted text-sm">Total Brands</p>
                <p className="text-2xl font-bold text-foreground">5</p>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-text-muted text-sm">Content Pieces</p>
                <p className="text-2xl font-bold text-foreground">120</p>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-text-muted text-sm">Avg Engagement</p>
                <p className="text-2xl font-bold text-foreground">4.8%</p>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-text-muted text-sm">Market Leader</p>
                <p className="text-2xl font-bold text-foreground">Coca-Cola</p>
              </div>
            </div>
            <div className="bg-card rounded-2xl p-6 border border-border">
              <h2 className="text-xl font-semibold text-foreground mb-4">Competitive Intelligence</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {['Content Strategy', 'Channel Performance', 'Market Share'].map((item) => (
                  <div key={item} className="bg-background-secondary rounded-lg p-4">
                    <h3 className="text-foreground font-medium mb-3">{item}</h3>
                    <div className="h-24 bg-background-tertiary rounded"></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-2xl p-6 border border-border h-64"></div>
              <div className="bg-card rounded-2xl p-6 border border-border h-64"></div>
            </div>
          </div>
        </div>

        {/* Coming Soon Lock Overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[2px] rounded-2xl">
          <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center">
              <i className="fas fa-clock text-primary text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">Coming Soon</h3>
            <p className="text-text-muted text-sm mb-6">
              Promotions & Campaigns module is currently under development. Track competitive intelligence, content analysis, and market positioning all in one place.
            </p>
            <a
              href="mailto:info@silainsights.com?subject=Interest in Promotions & Campaigns Module"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-full font-medium text-sm hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/25"
            >
              <i className="fas fa-bell"></i>
              Notify Me When Ready
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionsPageV2;